import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withAuthorization, withEmailVerification, AuthUserContext } from '../Session';
import { withFirebase } from '../Firebase';
import { useAppAlert } from '../AppAlertProvider';
import { getBrandingContext } from '../../modules/brandingMarketing/profileAdapter';
import { isFranchiseeEmail } from '../../utils/dashboardRouting';
import BrandKit from '../../modules/brandingMarketing/BrandKit';
import Printables from '../../modules/brandingMarketing/Printables';
import StudioRouter from '../../modules/studio/StudioRouter';
import AssetsExplorer from '../../modules/brandingMarketing/AssetsExplorer';
import ExclusiveMandates from '../../modules/exclusiveMandates/ExclusiveMandates';
import TrainingPage from '../../modules/academy/pages/TrainingPage';
import EventsPage from '../../modules/events/pages/EventsPage';
import TemplatesPage from '../../modules/templates/pages/TemplatesPage';
import TemplateEditorPage from '../../modules/templates/pages/TemplateEditorPage';
import DocumentsPage from '../../modules/templates/pages/DocumentsPage';
import SupportPage from '../support/SupportPage';
import CRMApp from '../../modules/crm/pages/CRMApp';
import { loginGreeting } from '../../lib/notifications/notificationsApi';
import { useNotificationsInbox, useUnreadCount } from '../../lib/notifications/notificationsHooks';
import { resolveNotificationDesign } from '../../lib/notifications/design';
import NotificationBell from '../Notifications/NotificationBell';
import NotificationsDrawer from '../Notifications/NotificationsDrawer';
import '../Dashboard/styles.css';

const sectionLabels = {
  home: 'Home',
  leads: 'Leads',
  'leads-crm': 'Leads / CRM',
  'leads-request': 'Leads / Request Lead/Listing',
  branding: 'Branding & Marketing Suite',
  'exclusive-mandates': 'Exclusive Mandates',
  events: 'Events',
  templates: 'Templates',
  support: 'Support',
  'academy-study': 'Academy / Study Material',
  'academy-training-online': 'Academy / Training / Online Training',
  'academy-training-onsite': 'Academy / Training / On site training',
};

const sectionDescriptions = {
  home: 'Your franchise overview, documents, and key details.',
  leads: 'Track, manage, and convert your franchise leads here.',
  'leads-crm': 'Manage your CRM pipeline and follow-ups.',
  'leads-request': 'Request new leads or listings from HQ.',
  branding: 'Access brand assets, marketing kits, and campaign playbooks.',
  'exclusive-mandates': 'Review exclusive mandates curated for your territory.',
  events: 'Stay updated on upcoming events and franchise meets.',
  templates: 'Download ready-to-use templates for your daily operations.',
  support: 'Reach out to the support team or raise a service request.',
  'academy-study': 'Study material, guides, and reference resources.',
  'academy-training-online': 'Online training modules and certification tracks.',
  'academy-training-onsite': 'On site training schedules and requests.',
};

const brandingBasePath = '/franchise-dashboard/branding-marketing';
const studioBasePath = '/franchise-dashboard/studio';
const templatesBasePath = '/franchise-dashboard/templates';

const FranchiseAccessDenied = () => {
  const history = useHistory();
  return (
    <div className="dashboard-container">
      <main className="dashboard-main" style={{ marginLeft: 0 }}>
        <div
          className="dashboard-content"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70vh',
          }}
        >
          <div className="franchise-access-card">
            <h2>This is only for franchise owners</h2>
            <p>Please use the generic dashboard if you are not a franchise owner.</p>
            <button
              type="button"
              className="franchise-access-button"
              onClick={() => history.push('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

const brandingTabs = [
  { key: 'overview', label: 'Overview', path: `${brandingBasePath}/overview` },
  { key: 'brand-kit', label: 'Brand Kit', path: `${brandingBasePath}/brand-kit` },
  { key: 'printables', label: 'Printables', path: `${brandingBasePath}/printables` },
  { key: 'studio', label: 'Studio (Beta)', path: studioBasePath },
  { key: 'assets', label: 'Assets', path: `${brandingBasePath}/assets` },
];

const formatFieldLabel = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFieldValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const resolveTimestamp = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const FranchiseDashboardPageBase = ({ firebase }) => {
  const authUser = useContext(AuthUserContext);
  const { showAlert } = useAppAlert();
  const history = useHistory();
  const location = useLocation();
  const isDarkMode =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark-mode');
  const [selectedSection, setSelectedSection] = useState('home');
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [academyOpen, setAcademyOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [brandingContext, setBrandingContext] = useState(null);
  const [brandingContextLoading, setBrandingContextLoading] = useState(false);
  const [brandingContextError, setBrandingContextError] = useState('');
  const [notificationToasts, setNotificationToasts] = useState([]);
  const [showArchivedNotifications, setShowArchivedNotifications] = useState(false);
  const [toastPosition, setToastPosition] = useState(null);
  const [linkedRequest, setLinkedRequest] = useState(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const [linkedProfile, setLinkedProfile] = useState(null);
  const [linkedProfileLoading, setLinkedProfileLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [loadingAgreement, setLoadingAgreement] = useState(true);
  const [showFranchiseeCard, setShowFranchiseeCard] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const profileDropdownRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const notificationToastContainerRef = useRef(null);
  const greetingTriggeredRef = useRef(false);
  const navStateRestored = useRef(false);
  const popupQueuedIdsRef = useRef(new Set());
  const toastTimersRef = useRef(new Map());
  const navStateStorageKey = 'franchiseDashboardNavState';
  const linkedUserId = linkedRequest?.userId || linkedRequest?.id || null;
  const linkedUserEmail = linkedRequest?.userEmail || linkedRequest?.email || null;
  const effectiveUserId = linkedUserId || authUser?.uid || null;
  const effectiveUserEmail = linkedUserEmail || authUser?.email || null;

  const activeInbox = useNotificationsInbox(firebase, {
    status: 'active',
    limit: 20,
    pollIntervalMs: 5000,
  });

  const archivedInbox = useNotificationsInbox(firebase, {
    status: 'archived',
    limit: 20,
    pollIntervalMs: showArchivedNotifications ? 5000 : 0,
  });

  const notifications = showArchivedNotifications
    ? archivedInbox.items
    : activeInbox.items;
  const markAllReadHandler = showArchivedNotifications ? null : activeInbox.markAllRead;
  const markReadHandler = showArchivedNotifications ? archivedInbox.markRead : activeInbox.markRead;
  const archiveHandler = showArchivedNotifications ? null : activeInbox.archive;
  const unarchiveHandler = showArchivedNotifications ? archivedInbox.unarchive : activeInbox.unarchive;
  const deleteHandler = showArchivedNotifications ? archivedInbox.remove : activeInbox.remove;

  const { count: unreadCount } = useUnreadCount(firebase, { pollIntervalMs: 5000 });

  const updateToastPosition = useCallback(() => {
    if (!notificationButtonRef.current) return;
    const rect = notificationButtonRef.current.getBoundingClientRect();
    const width = 320;
    const gap = 10;
    const viewportPadding = 12;
    let left = rect.right - width + 8;
    if (left < viewportPadding) left = viewportPadding;
    if (left + width > window.innerWidth - viewportPadding) {
      left = window.innerWidth - width - viewportPadding;
    }
    const top = rect.bottom + gap;
    const arrowLeft = Math.max(24, Math.min(width - 24, rect.right - left - 18));
    setToastPosition({
      top,
      left,
      width,
      arrowLeft,
    });
  }, []);

  const dismissToast = useCallback((id) => {
    setNotificationToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = toastTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(id);
    }
  }, []);

  const clearAllToasts = useCallback(() => {
    setNotificationToasts([]);
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
  }, []);

  const enqueueToast = useCallback((notification) => {
    const design = resolveNotificationDesign(notification);
    setNotificationToasts((prev) => {
      if (prev.some((toast) => toast.id === notification.id)) {
        return prev;
      }
      return [
        ...prev,
        {
          id: notification.id,
          title: notification.title,
          body: notification.body,
          data: notification.data || null,
          autoDismissMs: design.autoDismissMs || 7000,
        },
      ];
    });
  }, []);

  useEffect(() => {
    if (!Array.isArray(activeInbox.items)) return;
    const eligible = activeInbox.items
      .filter((item) => item?.popupEligible === true && !item.popupShownAt)
      .filter((item) => !popupQueuedIdsRef.current.has(item.id));
    if (!eligible.length) return;
    eligible
      .slice()
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      .forEach((item) => {
        popupQueuedIdsRef.current.add(item.id);
        enqueueToast(item);
        if (typeof activeInbox.markPopupSeen === 'function') {
          activeInbox.markPopupSeen(item.id);
        }
      });
  }, [activeInbox.items, activeInbox.markPopupSeen, enqueueToast]);

  useEffect(() => {
    notificationToasts.forEach((toast) => {
      if (toastTimersRef.current.has(toast.id)) return;
      const timer = setTimeout(() => dismissToast(toast.id), toast.autoDismissMs || 7000);
      toastTimersRef.current.set(toast.id, timer);
    });
    return () => undefined;
  }, [notificationToasts, dismissToast]);

  useEffect(() => {
    if (!notificationToasts.length) return undefined;
    updateToastPosition();
    const handleResize = () => updateToastPosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [notificationToasts.length, updateToastPosition]);

  useEffect(() => () => {
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
  }, []);

  if (authUser && !isFranchiseeEmail(authUser?.email)) {
    return <FranchiseAccessDenied />;
  }
  const normalizedPath =
    typeof location?.pathname === 'string'
      ? location.pathname.replace(/\/+$/, '')
      : '';
  const isBrandingRoute =
    normalizedPath.startsWith(brandingBasePath) || normalizedPath.startsWith(studioBasePath);
  const isStudioRoute =
    normalizedPath.startsWith(`${brandingBasePath}/studio`) ||
    normalizedPath.startsWith(studioBasePath);


  const cardSize = { width: 560, height: 340 };
  const cardTheme = {
    base: '#050505',
    surface: '#0c0c0c',
    border: 'rgba(255,255,255,0.14)',
    soft: 'rgba(255,255,255,0.08)',
    micro: 'rgba(255,255,255,0.55)',
    text: '#f8f8f8',
    muted: 'rgba(248,248,248,0.72)',
    faint: 'rgba(248,248,248,0.55)',
    shadow: '0 50px 120px rgba(0,0,0,0.85)',
    glow: 'rgba(255,255,255,0.06)',
    panel: 'rgba(255,255,255,0.02)',
    glass: 'rgba(255,255,255,0.06)',
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    if (savedDarkMode) {
      document.body.classList.add('dark-mode');
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
      if (
        showNotificationsDropdown &&
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setShowNotificationsDropdown(false);
      }
      if (
        notificationToasts.length &&
        notificationToastContainerRef.current &&
        !notificationToastContainerRef.current.contains(event.target) &&
        (!notificationButtonRef.current || !notificationButtonRef.current.contains(event.target))
      ) {
        clearAllToasts();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationsDropdown, notificationToasts.length, clearAllToasts]);

  useEffect(() => {
    if (typeof window === 'undefined' || navStateRestored.current) return;
    const savedState = window.localStorage.getItem(navStateStorageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.selectedSection) {
          setSelectedSection(parsed.selectedSection);
        }
        if (typeof parsed.leadsOpen === 'boolean') {
          setLeadsOpen(parsed.leadsOpen);
        }
        if (typeof parsed.brandingOpen === 'boolean') {
          setBrandingOpen(parsed.brandingOpen);
        }
        if (typeof parsed.academyOpen === 'boolean') {
          setAcademyOpen(parsed.academyOpen);
        }
        if (typeof parsed.trainingOpen === 'boolean') {
          setTrainingOpen(parsed.trainingOpen);
        }
      } catch (error) {
        console.error('Failed to restore franchise dashboard nav state:', error);
      }
    }
    navStateRestored.current = true;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !navStateRestored.current) return;
    const payload = {
      selectedSection,
      leadsOpen,
      brandingOpen,
      academyOpen,
      trainingOpen,
    };
    window.localStorage.setItem(navStateStorageKey, JSON.stringify(payload));
  }, [selectedSection, leadsOpen, brandingOpen, academyOpen, trainingOpen]);

  useEffect(() => {
    if (!isBrandingRoute) return;
    if (selectedSection !== 'branding') {
      setSelectedSection('branding');
    }
    setBrandingOpen(true);
  }, [isBrandingRoute, selectedSection]);

  useEffect(() => {
    if (!location?.pathname || isBrandingRoute) return;
    const normalized = location.pathname.replace(/\/+$/, '');
    const normalizedLower = normalized.toLowerCase();
    if (normalized === '/franchise-dashboard/home') {
      setSelectedSection('home');
      setLeadsOpen(false);
    } else if (
      normalized === '/franchise-dashboard/leads/CRM' ||
      normalizedLower === '/franchise-dashboard/leads/crm'
    ) {
      setSelectedSection('leads-crm');
      setLeadsOpen(true);
    } else if (normalizedLower === '/franchise-dashboard/leads/listing') {
      setSelectedSection('leads-crm');
      setLeadsOpen(true);
    } else if (normalizedLower === '/franchise-dashboard/leads/request') {
      setSelectedSection('leads-request');
      setLeadsOpen(true);
    } else if (normalized === '/franchise-dashboard/exclusive-mandate') {
      setSelectedSection('exclusive-mandates');
      setLeadsOpen(false);
    } else if (normalized === '/franchise-dashboard/events') {
      setSelectedSection('events');
      setLeadsOpen(false);
    } else if (normalized === '/franchise-dashboard/support') {
      setSelectedSection('support');
      setLeadsOpen(false);
    } else {
      setLeadsOpen(false);
    }
    if (normalized === '/franchise-dashboard/academy/training') {
      setSelectedSection('academy-training-online');
      setAcademyOpen(true);
      setTrainingOpen(true);
    } else if (normalized === '/franchise-dashboard/academy/training/onsite') {
      setSelectedSection('academy-training-onsite');
      setAcademyOpen(true);
      setTrainingOpen(true);
    } else if (normalized === '/franchise-dashboard/academy/study') {
      setSelectedSection('academy-study');
      setAcademyOpen(true);
      setTrainingOpen(false);
    }
    if (normalized.startsWith(templatesBasePath)) {
      setSelectedSection('templates');
    }
  }, [location?.pathname, isBrandingRoute]);

  useEffect(() => {
    if (!isBrandingRoute || !location?.pathname) return;
    const normalized = location.pathname.replace(/\/+$/, '');
    if (normalized === brandingBasePath) {
      history.replace(`${brandingBasePath}/overview`);
    }
  }, [history, isBrandingRoute, location?.pathname]);

  useEffect(() => {
    if (!authUser || !isBrandingRoute) {
      setBrandingContext(null);
      setBrandingContextError('');
      setBrandingContextLoading(false);
      return;
    }

    let mounted = true;
    setBrandingContextLoading(true);
    setBrandingContextError('');

    const loadContext = async () => {
      try {
        const ctx = await getBrandingContext(authUser);
        if (!mounted) return;
        setBrandingContext(ctx);
        const blocking = ctx?.blockingReasons || [];
        if (blocking.includes('CONTEXT_RESOLUTION_FAILED')) {
          setBrandingContextError('Unable to load branding context. Please try again.');
        } else if (
          blocking.includes('TENANT_NOT_RESOLVED') ||
          blocking.includes('BRANCH_NOT_RESOLVED')
        ) {
          setBrandingContextError('Tenant or branch details are missing. Update your profile.');
        }
      } catch (error) {
        if (!mounted) return;
        console.error('Branding context failed:', error);
        setBrandingContextError('Unable to load branding context.');
      } finally {
        if (mounted) {
          setBrandingContextLoading(false);
        }
      }
    };

    loadContext();
    return () => {
      mounted = false;
    };
  }, [authUser, isBrandingRoute]);

  useEffect(() => {
    if (!authUser?.email) {
      setLinkedRequest(null);
      setLinkLoading(false);
      return;
    }

    setLinkLoading(true);
    const requestQuery = firebase.db
      .collection('onboardingRequests')
      .where('assignedEmail', '==', authUser.email)
      .limit(1);

    const unsubscribe = requestQuery.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setLinkedRequest({ id: doc.id, ...doc.data() });
        } else {
          setLinkedRequest(null);
        }
        setLinkLoading(false);
      },
      (error) => {
        console.error('Error loading linked onboarding request:', error);
        setLinkedRequest(null);
        setLinkLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser?.email, firebase]);

  useEffect(() => {
    if (!effectiveUserId) {
      setLinkedProfile(null);
      setLinkedProfileLoading(false);
      return;
    }

    setLinkedProfileLoading(true);
    const unsubscribe = firebase.db
      .collection('users')
      .doc(effectiveUserId)
      .onSnapshot(
        (doc) => {
          setLinkedProfile(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setLinkedProfileLoading(false);
        },
        (error) => {
          console.error('Error loading linked profile:', error);
          setLinkedProfile(null);
          setLinkedProfileLoading(false);
        },
      );

    return () => unsubscribe();
  }, [effectiveUserId, firebase]);

  useEffect(() => {
    if (!effectiveUserId) {
      setAgreement(null);
      setLoadingAgreement(false);
      return;
    }

    setLoadingAgreement(true);

    const unsubscribe = firebase.db
      .collection('franchiseAgreements')
      .where('userId', '==', effectiveUserId)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.empty) {
            const agreements = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            agreements.sort((a, b) => {
              const timeA = a.createdAt?.toMillis() || 0;
              const timeB = b.createdAt?.toMillis() || 0;
              return timeB - timeA;
            });
            setAgreement(agreements[0]);
            setLoadingAgreement(false);
            return;
          }

          if (effectiveUserEmail) {
            firebase.db
              .collection('franchiseAgreements')
              .where('userEmail', '==', effectiveUserEmail)
              .get()
              .then((fallbackSnapshot) => {
                if (!fallbackSnapshot.empty) {
                  const agreements = fallbackSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                  }));
                  agreements.sort((a, b) => {
                    const timeA = a.createdAt?.toMillis() || 0;
                    const timeB = b.createdAt?.toMillis() || 0;
                    return timeB - timeA;
                  });
                  setAgreement(agreements[0]);
                } else {
                  setAgreement(null);
                }
                setLoadingAgreement(false);
              })
              .catch((error) => {
                console.error('Error loading agreement fallback:', error);
                setAgreement(null);
                setLoadingAgreement(false);
              });
          } else {
            setAgreement(null);
            setLoadingAgreement(false);
          }
        },
        (error) => {
          console.error('Error loading franchise agreements:', error);
          setAgreement(null);
          setLoadingAgreement(false);
        },
      );

    return () => unsubscribe();
  }, [effectiveUserId, effectiveUserEmail, firebase]);

  useEffect(() => {
    if (!authUser?.uid || greetingTriggeredRef.current) return;
    greetingTriggeredRef.current = true;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    loginGreeting(firebase, {
      timezone,
      tzOffsetMinutes,
      productName: 'Douglas & McCartney CRM',
    }).catch((error) => {
      console.error('Login greeting failed:', error);
    });
  }, [authUser?.uid, firebase]);

  const effectiveSection = isBrandingRoute ? 'branding' : selectedSection;
  const headerTitle = sectionLabels[effectiveSection] || 'Franchise Dashboard';

  const selectSection = (section) => {
    setSelectedSection(section);
    if (section.startsWith('leads-')) {
      setLeadsOpen(true);
    } else {
      setLeadsOpen(false);
    }
    if (section.startsWith('branding-')) {
      setBrandingOpen(true);
    } else if (section !== 'branding') {
      setBrandingOpen(false);
    }
    if (section.startsWith('academy')) {
      setAcademyOpen(true);
      setTrainingOpen(section.startsWith('academy-training'));
    } else {
      setAcademyOpen(false);
      setTrainingOpen(false);
    }
    if (section === 'home') {
      history.push('/franchise-dashboard/home');
      return;
    }
    if (section === 'branding') {
      history.push(`${brandingBasePath}/overview`);
      return;
    }
    if (isBrandingRoute) {
      history.push('/franchise-dashboard');
    }

    if (section === 'academy-training-online') {
      history.push('/franchise-dashboard/academy/training');
    }
    if (section === 'academy-training-onsite') {
      history.push('/franchise-dashboard/academy/training/onsite');
    }
    if (section === 'academy-study') {
      history.push('/franchise-dashboard/academy/study');
    }
    if (section === 'leads-crm') {
      history.push('/franchise-dashboard/leads/CRM');
    }
    if (section === 'leads-request') {
      history.push('/franchise-dashboard/leads/request');
    }
    if (section === 'branding-overview') {
      history.push(`${brandingBasePath}/overview`);
    }
    if (section === 'branding-brand-kit') {
      history.push(`${brandingBasePath}/brand-kit`);
    }
    if (section === 'branding-printables') {
      history.push(`${brandingBasePath}/printables`);
    }
    if (section === 'branding-studio') {
      history.push(studioBasePath);
    }
    if (section === 'branding-assets') {
      history.push(`${brandingBasePath}/assets`);
    }
    if (section === 'templates') {
      history.push(templatesBasePath);
    }
    if (section === 'exclusive-mandates') {
      history.push('/franchise-dashboard/exclusive-mandate');
    }
    if (section === 'events') {
      history.push('/franchise-dashboard/events');
    }
    if (section === 'support') {
      history.push('/franchise-dashboard/support');
    }
  };

  const toggleBranding = () => {
    setBrandingOpen((prev) => !prev);
  };

  const toggleLeads = () => {
    setLeadsOpen((prev) => !prev);
  };

  const toggleAcademy = () => {
    setAcademyOpen((prev) => {
      const next = !prev;
      if (!next) {
        setTrainingOpen(false);
      }
      return next;
    });
  };

  const toggleTraining = () => {
    setAcademyOpen(true);
    setTrainingOpen((prev) => !prev);
  };

  const handleSignOut = () => {
    firebase.doSignOut();
  };

  const brandingStatusNote = useMemo(() => {
    if (!isBrandingRoute) return null;
    if (brandingContextLoading) {
      return 'Loading branding context...';
    }
    if (brandingContextError) {
      return brandingContextError;
    }
    if (!brandingContext?.tenantId || !brandingContext?.branchId) {
      return 'Branding context is incomplete. Update your profile to unlock assets.';
    }
    return null;
  }, [brandingContext?.branchId, brandingContext?.tenantId, brandingContextError, brandingContextLoading, isBrandingRoute]);

  const openNotificationsPanel = useCallback(() => {
    setShowArchivedNotifications(false);
    setShowNotificationsDropdown(true);
    activeInbox.refresh();
    activeInbox.markAllRead();
    clearAllToasts();
  }, [activeInbox, clearAllToasts]);

  const toggleNotificationsDropdown = () => {
    openNotificationsPanel();
  };

  const toggleArchivedView = useCallback(() => {
    setShowArchivedNotifications((prev) => {
      const next = !prev;
      if (next) {
        archivedInbox.refresh();
      } else {
        activeInbox.refresh();
      }
      return next;
    });
  }, [activeInbox, archivedInbox]);

  const signedDocBucket =
    agreement?.finalPdfBucket || agreement?.eStampSignedDocumentBucket || null;
  const signedDocKey =
    agreement?.finalPdfKey || agreement?.eStampSignedDocumentPath || null;
  const signedDocToken =
    agreement?.finalPdfDownloadToken || agreement?.eStampSignedDocumentDownloadToken || null;
  const signedDocUrl =
    agreement?.finalPdfUrl ||
    agreement?.eStampSignedDocumentDownloadUrl ||
    (signedDocKey && signedDocBucket
      ? `https://firebasestorage.googleapis.com/v0/b/${signedDocBucket}/o/${encodeURIComponent(
        signedDocKey,
      )}?alt=media${signedDocToken ? `&token=${signedDocToken}` : ''}`
      : null);
  const signedDocOnFile = Boolean(
    agreement?.manualSignedDocumentUploaded || signedDocUrl || agreement?.finalPdfKey,
  );

  const franchiseIdSegment = (authUser?.uid?.substring(0, 10) || 'XXXXXXXXXX').toUpperCase();
  const fallbackFranchiseeId = `DM-${franchiseIdSegment}`;
  const normalizeFranchiseeId = (value, fallback) => {
    const raw = value ? String(value).trim() : '';
    if (!raw) {
      return fallback;
    }
    const upper = raw.toUpperCase();
    return upper.startsWith('DM-') ? upper : `DM-${upper}`;
  };
  const franchiseeIdDisplay = normalizeFranchiseeId(
    agreement?.franchiseeId,
    fallbackFranchiseeId,
  );
  const franchiseeIdFilename = franchiseeIdDisplay.replace(/[^A-Z0-9-]/g, '');

  const cardIssuedAt = resolveTimestamp(agreement?.franchiseeCardGeneratedAt);
  const cardIssuedDate = cardIssuedAt
    ? cardIssuedAt
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      .toUpperCase()
    : null;
  const cardValidThroughAt = cardIssuedAt ? new Date(cardIssuedAt.getTime()) : null;
  if (cardValidThroughAt) {
    cardValidThroughAt.setFullYear(cardValidThroughAt.getFullYear() + 5);
  }
  const cardValidThroughDate = cardValidThroughAt
    ? cardValidThroughAt
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      .toUpperCase()
    : null;
  const cardValidityDisplay =
    cardIssuedDate && cardValidThroughDate ? (
      <>
        {cardIssuedDate}
        <br />
        <span style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '1px', color: cardTheme.faint }}>
          to
        </span>
        <br />
        {cardValidThroughDate}
      </>
    ) : (
      'PENDING'
    );

  const cardGenerated = Boolean(
    agreement?.franchiseeCardGeneratedAt || agreement?.franchiseeCardGenerated,
  );

  const openSignedDoc = () => {
    if (!signedDocUrl) return;
    window.open(signedDocUrl, '_blank', 'noopener');
  };

  const openFranchiseeCard = () => {
    if (!cardGenerated) {
      alert('Franchisee card is not generated yet.');
      return;
    }
    setShowFranchiseeCard(true);
  };

  const downloadFranchiseeCardPdf = async () => {
    const front = document.getElementById('franchisee-card-front');
    const back = document.getElementById('franchisee-card-back');

    if (!front || !back) {
      return;
    }

    const html2canvas = (await import('html2canvas')).default;
    const captureFace = async (face) => {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.width = `${cardSize.width}px`;
      wrapper.style.height = `${cardSize.height}px`;
      wrapper.style.padding = '0';
      wrapper.style.margin = '0';
      wrapper.style.overflow = 'hidden';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.background = 'transparent';
      wrapper.style.transform = 'none';
      wrapper.style.perspective = 'none';
      wrapper.style.zIndex = '9999';

      const clone = face.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.inset = '0';
      clone.style.transform = 'none';
      clone.style.backfaceVisibility = 'visible';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        backgroundColor: null,
        scale: 3,
        logging: false,
      });

      wrapper.remove();
      return canvas;
    };

    const frontCanvas = await captureFace(front);
    const backCanvas = await captureFace(back);

    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [cardSize.width, cardSize.height],
    });

    pdf.addImage(frontCanvas, 'PNG', 0, 0, cardSize.width, cardSize.height);
    pdf.addPage([cardSize.width, cardSize.height], 'landscape');
    pdf.addImage(backCanvas, 'PNG', 0, 0, cardSize.width, cardSize.height);
    pdf.save(`Douglas-McCartney-Franchisee-Card-${franchiseeIdFilename || franchiseIdSegment}.pdf`);
  };

  const formDetails = useMemo(() => {
    const formData = agreement?.formData || {};
    const entries = [];
    const usedKeys = new Set();
    const preferredOrder = [
      'fullName',
      'email',
      'phone',
      'mobile',
      'city',
      'address',
      'companyName',
      'businessName',
      'reraNumber',
      'panNumber',
      'gstNumber',
    ];

    const addEntry = (key, value) => {
      if (value === null || value === undefined || String(value).trim() === '') return;
      usedKeys.add(key);
      entries.push({
        label: formatFieldLabel(key),
        value: formatFieldValue(value),
      });
    };

    preferredOrder.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(formData, key)) {
        addEntry(key, formData[key]);
      }
    });

    Object.keys(formData)
      .filter((key) => !usedKeys.has(key))
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => addEntry(key, formData[key]));

    if (agreement?.franchiseeId) {
      entries.unshift({
        label: 'Franchisee Id',
        value: franchiseeIdDisplay,
      });
    }

    return entries;
  }, [agreement, franchiseeIdDisplay]);

  const profileFieldConfig = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'organization', label: 'Organization' },
    { key: 'location', label: 'Location' },
    { key: 'licenseNumber', label: 'License Number' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'experience', label: 'Experience' },
    { key: 'specialization', label: 'Specialization' },
    { key: 'certifications', label: 'Certifications' },
  ];

  const profileDetails = useMemo(() => {
    const details = [];
    profileFieldConfig.forEach(({ key, label }) => {
      let value = linkedProfile?.[key] || null;
      if (!value) {
        if (key === 'fullName') {
          value = linkedRequest?.fullName || agreement?.formData?.fullName || null;
        }
        if (key === 'phoneNumber') {
          value =
            linkedRequest?.phone ||
            agreement?.formData?.phone ||
            agreement?.formData?.mobile ||
            null;
        }
        if (key === 'location') {
          value = agreement?.formData?.city || null;
        }
      }
      if (value) {
        details.push({ label, value });
      }
    });
    return details;
  }, [linkedProfile, linkedRequest, agreement]);

  const displayName =
    linkedProfile?.fullName ||
    linkedRequest?.fullName ||
    agreement?.formData?.fullName ||
    authUser?.displayName ||
    authUser?.username ||
    (authUser?.email ? authUser.email.split('@')[0] : 'Franchisee');
  const displayEmail =
    authUser?.email ||
    linkedRequest?.assignedEmail ||
    'N/A';
  const profileLoading = loadingAgreement || linkLoading || linkedProfileLoading;
  const effectiveDetails = profileDetails.length > 0 ? profileDetails : formDetails;

  if (!authUser) {
    return null;
  }

  return (
    <div className="dashboard-container">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo">
          <img
            src="/Douglas2.png"
            alt="Douglas & McCartney"
            className="sidebar-logo-img"
          />
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <button
              onClick={() => selectSection('home')}
              className={`nav-button nav-home-button ${selectedSection === 'home' ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Home</span>
            </button>
          </div>

          <div className="nav-section">
            <button
              className={`nav-button ${leadsOpen ? 'active' : ''}`}
              onClick={toggleLeads}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Leads</span>
              <svg className={`dropdown-arrow ${leadsOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {leadsOpen && (
              <div className="nav-dropdown">
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('leads-crm')}
                  style={{
                    background:
                      selectedSection === 'leads-crm' ? 'rgba(168, 168, 168, 0.12)' : 'transparent',
                    color: selectedSection === 'leads-crm' ? '#f8f8f8' : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  CRM
                </button>
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('leads-request')}
                  style={{
                    background:
                      selectedSection === 'leads-request' ? 'rgba(168, 168, 168, 0.12)' : 'transparent',
                    color: selectedSection === 'leads-request' ? '#f8f8f8' : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Request Lead/Listing
                </button>
              </div>
            )}
          </div>

          <div className="nav-section">
            <button
              className={`nav-button ${brandingOpen ? 'active' : ''}`}
              onClick={toggleBranding}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
              </svg>
              <span className="nav-label-nowrap">Branding & Marketing Suite</span>
              <svg className={`dropdown-arrow ${brandingOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {brandingOpen && (
              <div className="nav-dropdown">
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('branding-overview')}
                  style={{
                    background:
                      normalizedPath.startsWith(`${brandingBasePath}/overview`)
                        ? 'rgba(168, 168, 168, 0.12)'
                        : 'transparent',
                    color:
                      normalizedPath.startsWith(`${brandingBasePath}/overview`)
                        ? '#f8f8f8'
                        : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Overview
                </button>
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('branding-brand-kit')}
                  style={{
                    background:
                      normalizedPath.startsWith(`${brandingBasePath}/brand-kit`)
                        ? 'rgba(168, 168, 168, 0.12)'
                        : 'transparent',
                    color:
                      normalizedPath.startsWith(`${brandingBasePath}/brand-kit`)
                        ? '#f8f8f8'
                        : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Brand Kit
                </button>
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('branding-printables')}
                  style={{
                    background:
                      normalizedPath.startsWith(`${brandingBasePath}/printables`)
                        ? 'rgba(168, 168, 168, 0.12)'
                        : 'transparent',
                    color:
                      normalizedPath.startsWith(`${brandingBasePath}/printables`)
                        ? '#f8f8f8'
                        : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Printables
                </button>
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('branding-studio')}
                  style={{
                    background:
                      normalizedPath.startsWith(studioBasePath) ||
                        normalizedPath.startsWith(`${brandingBasePath}/studio`)
                        ? 'rgba(168, 168, 168, 0.12)'
                        : 'transparent',
                    color:
                      normalizedPath.startsWith(studioBasePath) ||
                        normalizedPath.startsWith(`${brandingBasePath}/studio`)
                        ? '#f8f8f8'
                        : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Studio (Beta)
                </button>
                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('branding-assets')}
                  style={{
                    background:
                      normalizedPath.startsWith(`${brandingBasePath}/assets`)
                        ? 'rgba(168, 168, 168, 0.12)'
                        : 'transparent',
                    color:
                      normalizedPath.startsWith(`${brandingBasePath}/assets`)
                        ? '#f8f8f8'
                        : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Assets
                </button>
              </div>
            )}
          </div>

          <div className="nav-section">
            <button
              className={`nav-button ${academyOpen ? 'active' : ''}`}
              onClick={toggleAcademy}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6l7 3-7 3-7-3 7-3z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v6l7 3 7-3v-6" />
              </svg>
              <span>Academy</span>
              <svg className={`dropdown-arrow ${academyOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {academyOpen && (
              <div className="nav-dropdown">
                <div className="nav-sub-section">
                  <button
                    className={`nav-sub-button ${trainingOpen ? 'active' : ''}`}
                    onClick={toggleTraining}
                  >
                    <span>Training</span>
                    <svg className={`dropdown-arrow ${trainingOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {trainingOpen && (
                    <div className="nav-sub-dropdown">
                      <button
                        className="nav-item nav-item-button"
                        onClick={() => selectSection('academy-training-online')}
                        style={{
                          background:
                            selectedSection === 'academy-training-online' ? 'rgba(168, 168, 168, 0.12)' : 'transparent',
                          color:
                            selectedSection === 'academy-training-online' ? '#f8f8f8' : 'rgba(255, 255, 255, 0.65)',
                        }}
                      >
                        Online Training
                      </button>
                      <button
                        className="nav-item nav-item-button"
                        onClick={() => selectSection('academy-training-onsite')}
                        style={{
                          background:
                            selectedSection === 'academy-training-onsite' ? 'rgba(168, 168, 168, 0.12)' : 'transparent',
                          color:
                            selectedSection === 'academy-training-onsite' ? '#f8f8f8' : 'rgba(255, 255, 255, 0.65)',
                        }}
                      >
                        On site training
                      </button>
                    </div>
                  )}
                </div>

                <button
                  className="nav-item nav-item-button"
                  onClick={() => selectSection('academy-study')}
                  style={{
                    background:
                      selectedSection === 'academy-study' ? 'rgba(168, 168, 168, 0.12)' : 'transparent',
                    color: selectedSection === 'academy-study' ? '#f8f8f8' : 'rgba(255, 255, 255, 0.65)',
                  }}
                >
                  Study Material
                </button>
              </div>
            )}
          </div>

          <div className="nav-section">
            <button
              onClick={() => selectSection('exclusive-mandates')}
              className={`nav-button ${selectedSection === 'exclusive-mandates' ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
              </svg>
              <span>Exclusive Mandates</span>
            </button>
          </div>

          <div className="nav-section">
            <button
              onClick={() => selectSection('events')}
              className={`nav-button ${selectedSection === 'events' ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-12 8h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Events</span>
            </button>
          </div>

          <div className="nav-section">
            <button
              onClick={() => selectSection('templates')}
              className={`nav-button ${selectedSection === 'templates' ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
              </svg>
              <span>Templates</span>
            </button>
          </div>

          <div className="nav-section">
            <button
              onClick={() => selectSection('support')}
              className={`nav-button ${selectedSection === 'support' ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-1.414 1.414M6.343 17.657l-1.414 1.414m13.435 0l-1.414-1.414M6.343 6.343L4.929 4.929m13.435 6.364H20m-4 0a4 4 0 11-8 0 4 4 0 018 0zM4 12h2" />
              </svg>
              <span>Support</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-signout" onClick={handleSignOut}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        {notificationToasts.length > 0 && (
          <div
            className="notifications-toast-container"
            role="status"
            aria-live="polite"
            ref={notificationToastContainerRef}
            style={toastPosition ? {
              top: `${toastPosition.top}px`,
              left: `${toastPosition.left}px`,
              width: `${toastPosition.width}px`,
              '--toast-arrow-left': `${toastPosition.arrowLeft}px`,
            } : undefined}
          >
            {notificationToasts.map((toast) => {
              const design = resolveNotificationDesign(toast);
              const hasPrimaryCta = Boolean(design.ctaLabel);
              const hasSecondaryCta = Boolean(design.secondaryCtaLabel);
              const rich = Boolean(
                design.imageUrl ||
                design.badge ||
                design.eyebrow ||
                design.subtitle ||
                design.footer ||
                design.icon ||
                hasPrimaryCta ||
                hasSecondaryCta ||
                design.preset !== 'classic',
              );
              const mediaOnRight = design.imagePosition === 'right';
              const contentJustify =
                design.contentAlign === 'center'
                  ? 'center'
                  : design.contentAlign === 'end'
                    ? 'flex-end'
                    : 'flex-start';
              const textAlign =
                design.textAlign === 'center' || design.textAlign === 'right'
                  ? design.textAlign
                  : 'left';
              return (
                <div
                  key={toast.id}
                  className={`notifications-toast ${rich ? 'notifications-toast-rich' : ''} notifications-toast-${design.preset} notification-tone-${design.tone} notification-button-${design.buttonStyle} notification-image-${design.imagePosition}`}
                  style={{
                    ...(design.accentColor ? { '--notification-accent': design.accentColor } : {}),
                    ...(design.backgroundColor ? { '--notification-bg': design.backgroundColor } : {}),
                    ...(design.textColor ? { '--notification-text': design.textColor } : {}),
                    ...(design.bodyColor ? { '--notification-body': design.bodyColor } : {}),
                    '--notification-content-justify': contentJustify,
                    textAlign,
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    openNotificationsPanel();
                    activeInbox.markRead(toast.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openNotificationsPanel();
                      activeInbox.markRead(toast.id);
                    }
                  }}
                >
                  {design.imageUrl && !mediaOnRight && (
                    <div className="notifications-toast-media">
                      <img
                        src={design.imageUrl}
                        alt=""
                        loading="lazy"
                        style={{ objectFit: design.imageFit || 'cover' }}
                        referrerPolicy="no-referrer"
                        onError={(event) => {
                          const media = event.currentTarget.closest('.notifications-toast-media');
                          if (media) media.classList.add('is-broken');
                        }}
                      />
                    </div>
                  )}
                  <div className="notifications-toast-main">
                    {(design.icon || design.badge || design.eyebrow) && (
                      <div className="notification-meta-row">
                        {design.icon && <span className="notification-icon-chip">{design.icon}</span>}
                        {design.badge && <span className="notification-badge-chip">{design.badge}</span>}
                        {design.eyebrow && <div className="notifications-toast-eyebrow">{design.eyebrow}</div>}
                      </div>
                    )}
                    <div className="notifications-toast-title">{toast.title || 'Notification'}</div>
                    {design.subtitle && <div className="notification-subtitle">{design.subtitle}</div>}
                    <div className="notifications-toast-body">{toast.body}</div>
                    {(hasPrimaryCta || hasSecondaryCta) && (
                      <div className="notification-cta-row">
                        {hasPrimaryCta && (
                          design.ctaUrl ? (
                            <a
                              className="notifications-toast-link"
                              href={design.ctaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {design.ctaLabel}
                            </a>
                          ) : (
                            <span className="notifications-toast-link disabled" aria-disabled="true">
                              {design.ctaLabel}
                            </span>
                          )
                        )}
                        {hasSecondaryCta && (
                          design.secondaryCtaUrl ? (
                            <a
                              className="notifications-toast-link secondary"
                              href={design.secondaryCtaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {design.secondaryCtaLabel}
                            </a>
                          ) : (
                            <span
                              className="notifications-toast-link secondary disabled"
                              aria-disabled="true"
                            >
                              {design.secondaryCtaLabel}
                            </span>
                          )
                        )}
                      </div>
                    )}
                    {design.footer && <div className="notification-footer">{design.footer}</div>}
                    <div className="notifications-toast-actions">
                      <button
                        type="button"
                        className="notifications-toast-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          openNotificationsPanel();
                          activeInbox.markRead(toast.id);
                        }}
                      >
                        Open inbox
                      </button>
                      <button
                        type="button"
                        className="notifications-toast-action ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          dismissToast(toast.id);
                        }}
                      >
                        Dismiss
                        </button>
                    </div>
                  </div>
                  {design.imageUrl && mediaOnRight && (
                    <div className="notifications-toast-media right">
                      <img
                        src={design.imageUrl}
                        alt=""
                        loading="lazy"
                        style={{ objectFit: design.imageFit || 'cover' }}
                        referrerPolicy="no-referrer"
                        onError={(event) => {
                          const media = event.currentTarget.closest('.notifications-toast-media');
                          if (media) media.classList.add('is-broken');
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <header className="dashboard-header">
          <div className="header-content">
            <h1 className="header-title">{headerTitle}</h1>
            <div className="header-actions">
              <div className="header-notifications">
                <NotificationBell
                  count={unreadCount}
                  onClick={toggleNotificationsDropdown}
                  buttonRef={notificationButtonRef}
                />
                {showNotificationsDropdown && (
                  <NotificationsDrawer
                    items={notifications}
                    onMarkAllRead={markAllReadHandler}
                    onMarkRead={markReadHandler}
                    onArchive={archiveHandler}
                    onUnarchive={unarchiveHandler}
                    onDelete={deleteHandler}
                    showArchived={showArchivedNotifications}
                    onToggleArchived={toggleArchivedView}
                    dropdownRef={notificationDropdownRef}
                  />
                )}
              </div>

              <div className="profile-dropdown-container" ref={profileDropdownRef}>
                <button
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  className="user-info user-info-button"
                >
                  <div className="user-details">
                    <span className="user-name">{displayName}</span>
                    <span className="user-email">{displayEmail}</span>
                  </div>
                  <div className="user-avatar">
                    {(displayName || 'U').charAt(0).toUpperCase()}
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="profile-dropdown-menu">
                    <a href="/franchise-dashboard" className="profile-menu-item">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span>Dashboard</span>
                    </a>

                    <a href="/account" className="profile-menu-item">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Profile</span>
                    </a>

                    <a href="/settings" className="profile-menu-item">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Settings</span>
                    </a>

                    <button onClick={handleSignOut} className="profile-menu-item">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {effectiveSection === 'home' ? (
            <>
              <div className="welcome-section">
                <div className="welcome-header">
                  <svg className="welcome-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h2>Welcome to your Franchise Dashboard</h2>
                </div>
                <p className="welcome-text">
                  Everything we have on file for your franchise is organized here, along with your signed documents and card.
                </p>
              </div>

              <div className="profile-overview">
                <div className="profile-header">
                  <div className="profile-avatar-large">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-info">
                    <h3 className="profile-name">{displayName}</h3>
                    <p className="profile-email">{displayEmail}</p>
                    <div className="profile-badges">
                      <span className="badge badge-role">Franchisee</span>
                      <span className="badge badge-role">{franchiseeIdDisplay}</span>
                    </div>
                  </div>
                </div>

                {profileLoading ? (
                  <div className="empty-state">
                    <p>Loading your franchise details...</p>
                  </div>
                ) : effectiveDetails.length > 0 ? (
                  <div className="profile-details-grid">
                    {effectiveDetails.map((detail) => (
                      <div
                        key={detail.label}
                        className="profile-detail-card"
                        style={
                          isDarkMode
                            ? {
                              background: 'linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
                              borderColor: 'rgba(255,255,255,0.08)',
                            }
                            : undefined
                        }
                      >
                        <div
                          className="detail-icon"
                          style={
                            isDarkMode
                              ? { background: 'rgba(255,255,255,0.08)', color: '#f8f8f8' }
                              : undefined
                          }
                        >
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="detail-content">
                          <span
                            className="detail-label"
                            style={isDarkMode ? { color: 'rgba(248,248,248,0.6)' } : undefined}
                          >
                            {detail.label}
                          </span>
                          <span
                            className="detail-value"
                            style={isDarkMode ? { color: '#f8f8f8' } : undefined}
                          >
                            {detail.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No franchise details found yet.</p>
                  </div>
                )}
              </div>

              <div className="stats-grid">
                <div
                  className="stat-card"
                  style={
                    isDarkMode
                      ? { background: '#0a0a0a', borderColor: 'rgba(255,255,255,0.12)' }
                      : undefined
                  }
                >
                  <div className="stat-icon broker">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <h3 style={isDarkMode ? { color: '#f8f8f8' } : undefined}>Franchisee Card</h3>
                    <p style={isDarkMode ? { color: 'rgba(248,248,248,0.7)' } : undefined}>
                      {cardGenerated
                        ? `Generated${cardIssuedDate ? ` on ${cardIssuedDate}` : ''}.`
                        : 'Your franchisee card will appear here once it is generated.'}
                    </p>
                    <button
                      type="button"
                      className="btn-complete-profile"
                      onClick={openFranchiseeCard}
                      disabled={!cardGenerated}
                      style={{
                        marginTop: '16px',
                        opacity: cardGenerated ? 1 : 0.5,
                        cursor: cardGenerated ? 'pointer' : 'not-allowed',
                        borderColor: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                        color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                      }}
                    >
                      View Franchisee Card
                    </button>
                  </div>
                </div>

                <div
                  className="stat-card"
                  style={
                    isDarkMode
                      ? { background: '#0a0a0a', borderColor: 'rgba(255,255,255,0.12)' }
                      : undefined
                  }
                >
                  <div className="stat-icon customer">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <h3 style={isDarkMode ? { color: '#f8f8f8' } : undefined}>Signed Agreement</h3>
                    <p style={isDarkMode ? { color: 'rgba(248,248,248,0.7)' } : undefined}>
                      {signedDocOnFile
                        ? 'Your signed franchise agreement is available.'
                        : 'Waiting for the signed agreement to be attached.'}
                    </p>
                    <button
                      type="button"
                      className="btn-complete-profile"
                      onClick={openSignedDoc}
                      disabled={!signedDocOnFile}
                      style={{
                        marginTop: '16px',
                        opacity: signedDocOnFile ? 1 : 0.5,
                        cursor: signedDocOnFile ? 'pointer' : 'not-allowed',
                        borderColor: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                        color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                      }}
                    >
                      View Signed Document
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : effectiveSection === 'branding' ? (
            <>
              <div className="builder-forms-header">
                <h1>Branding &amp; Marketing Suite</h1>
                <p className="builder-section-subtitle">
                  Access brand assets, marketing kits, and campaign playbooks.
                </p>
              </div>

              <Switch>
                <Route exact path={`${brandingBasePath}/overview`}>
                  <div className="welcome-section">
                    <div className="overview-sections">
                      <p className="overview-sections-title">Section Guide</p>
                      <div className="overview-sections-grid">
                        <div className="overview-section-card">
                          <p className="overview-section-label">Brand Kit</p>
                          <p className="overview-section-desc">
                            Official logos, brand colors, and usage guidelines.
                          </p>
                          <button
                            type="button"
                            className="overview-section-action"
                            onClick={() => history.push(`${brandingBasePath}/brand-kit`)}
                          >
                            Go to Brand Kit
                          </button>
                        </div>
                        <div className="overview-section-card">
                          <p className="overview-section-label">Printables</p>
                          <p className="overview-section-desc">
                            Business cards, letterheads, and QR posters.
                          </p>
                          <button
                            type="button"
                            className="overview-section-action"
                            onClick={() => history.push(`${brandingBasePath}/printables`)}
                          >
                            Go to Printables
                          </button>
                        </div>
                        <div className="overview-section-card">
                          <p className="overview-section-label">Studio (Beta)</p>
                          <p className="overview-section-desc">
                            Customize WhatsApp flyers and Instagram creatives. This section is in beta.
                          </p>
                          <button
                            type="button"
                            className="overview-section-action"
                            onClick={() => history.push(`${brandingBasePath}/studio`)}
                          >
                            Go to Studio (Beta)
                          </button>
                        </div>
                        <div className="overview-section-card">
                          <p className="overview-section-label">Assets</p>
                          <p className="overview-section-desc">
                            View and manage shared Google Drive assets.
                          </p>
                          <button
                            type="button"
                            className="overview-section-action"
                            onClick={() => history.push(`${brandingBasePath}/assets`)}
                          >
                            Go to Assets
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Route>

                <Route exact path={`${brandingBasePath}/brand-kit`}>
                  <BrandKit
                    firebase={firebase}
                    brandingContext={brandingContext}
                    authUser={authUser}
                  />
                </Route>

                <Route exact path={`${brandingBasePath}/printables`}>
                  <Printables
                    firebase={firebase}
                    authUser={authUser}
                    brandingContext={brandingContext}
                    linkedProfile={linkedProfile}
                    linkedRequest={linkedRequest}
                    agreement={agreement}
                  />
                </Route>

                <Route path={studioBasePath}>
                  <StudioRouter
                    firebase={firebase}
                    authUser={authUser}
                    brandingContext={brandingContext}
                    brandingContextLoading={brandingContextLoading}
                    brandingContextError={brandingContextError}
                    basePath={studioBasePath}
                  />
                </Route>

                <Route path={`${brandingBasePath}/studio`}>
                  <StudioRouter
                    firebase={firebase}
                    authUser={authUser}
                    brandingContext={brandingContext}
                    brandingContextLoading={brandingContextLoading}
                    brandingContextError={brandingContextError}
                  />
                </Route>

                <Route exact path={`${brandingBasePath}/assets`}>
                  <AssetsExplorer firebase={firebase} />
                </Route>

                <Redirect to={`${brandingBasePath}/overview`} />
              </Switch>
            </>
          ) : effectiveSection === 'academy-training-online' ? (
            <TrainingPage firebase={firebase} />
          ) : effectiveSection === 'events' ? (
            <EventsPage firebase={firebase} />
          ) : effectiveSection === 'templates' ? (
            <Switch>
              <Route exact path={templatesBasePath}>
                <TemplatesPage firebase={firebase} authUser={authUser} />
              </Route>
              <Route exact path={`${templatesBasePath}/documents`}>
                <DocumentsPage firebase={firebase} authUser={authUser} />
              </Route>
              <Route path={`${templatesBasePath}/:templateId`}>
                <TemplateEditorPage firebase={firebase} authUser={authUser} />
              </Route>
              <Redirect to={templatesBasePath} />
            </Switch>
          ) : effectiveSection === 'support' ? (
            <SupportPage firebase={firebase} authUser={authUser} showAlert={showAlert} />
          ) : effectiveSection === 'exclusive-mandates' ? (
            <ExclusiveMandates firebase={firebase} />
          ) : (
            <div className="welcome-section">
              <div className="welcome-header">
                <svg className="welcome-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h2>{sectionLabels[effectiveSection] || 'Dashboard'}</h2>
              </div>
              <p className="welcome-text">
                {sectionDescriptions[effectiveSection] || 'Content will appear here soon.'}
              </p>
              {effectiveSection === 'leads-crm' && (
                <button
                  type="button"
                  onClick={() => {
                    // Set localStorage flag to grant CRM access (shared across tabs)
                    localStorage.setItem('crm_access_granted', Date.now().toString());
                    window.open('/CRM/franchise-crm/dashboard', '_blank', 'noopener,noreferrer');
                  }}
                  style={{
                    marginTop: '24px',
                    padding: '14px 32px',
                    background: '#f8f8f8',
                    color: '#0a0a0a',
                    border: 'none',
                    borderRadius: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#0a0a0a';
                    e.currentTarget.style.color = '#f8f8f8';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#f8f8f8';
                    e.currentTarget.style.color = '#0a0a0a';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open CRM
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {showFranchiseeCard && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease',
            padding: '20px',
          }}
          onClick={() => setShowFranchiseeCard(false)}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'relative',
              animation: 'slideUp 0.3s ease',
            }}
          >
            <button
              onClick={() => setShowFranchiseeCard(false)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                width: '40px',
                height: '40px',
                borderRadius: 0,
                background: '#1a1a1a',
                color: 'white',
                border: '2px solid #333',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                zIndex: 1,
                transition: 'all 0.3s ease',
                fontFamily:
                  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
              onMouseOver={(event) => {
                event.target.style.background = '#000';
                event.target.style.borderColor = '#555';
              }}
              onMouseOut={(event) => {
                event.target.style.background = '#1a1a1a';
                event.target.style.borderColor = '#333';
              }}
              aria-label="Close card"
            >
              X
            </button>

            <link
              href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Inter:wght@400;500;600;700;800&display=swap"
              rel="stylesheet"
            />

            <div
              id="franchisee-card"
              style={{
                width: 'min(560px, 92vw)',
                aspectRatio: '28 / 17',
                height: 'auto',
                perspective: '2000px',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setIsCardHovered(true)}
              onMouseLeave={() => setIsCardHovered(false)}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
                  transform: isCardHovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}
              >
                <div
                  id="franchisee-card-front"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '26px',
                    padding: 'clamp(22px, 4vw, 36px) clamp(24px, 4.5vw, 42px)',
                    background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 45%, ${cardTheme.base} 100%)`,
                    boxShadow: cardTheme.shadow,
                    border: `1px solid ${cardTheme.border}`,
                    overflow: 'hidden',
                    color: cardTheme.text,
                    fontFamily:
                      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backfaceVisibility: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `radial-gradient(circle at 20% 20%, ${cardTheme.glow}, transparent 52%), linear-gradient(115deg, ${cardTheme.soft} 0%, transparent 50%)`,
                      opacity: 0.85,
                      pointerEvents: 'none',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `linear-gradient(90deg, ${cardTheme.soft} 1px, transparent 1px), linear-gradient(180deg, ${cardTheme.soft} 1px, transparent 1px)`,
                      backgroundSize: '32px 32px',
                      opacity: 0.2,
                      pointerEvents: 'none',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '-40%',
                      right: '-10%',
                      width: '60%',
                      height: '200%',
                      background: `linear-gradient(120deg, ${cardTheme.soft}, transparent 60%)`,
                      opacity: 0.55,
                      transform: 'rotate(12deg)',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: '18px',
                      borderRadius: '20px',
                      border: `1px solid ${cardTheme.soft}`,
                      opacity: 0.45,
                      pointerEvents: 'none',
                    }}
                  ></div>

                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transform: 'translateY(-26px)',
                    }}
                  >
                    <div style={{ marginTop: 0 }}>
                      <p
                        style={{
                          margin: '10px 0 0 0',
                          fontSize: 'clamp(22px, 2.6vw, 30px)',
                          fontWeight: 500,
                          letterSpacing: '1.5px',
                          fontFamily: "'Cormorant Garamond', serif",
                          textTransform: 'capitalize',
                          transform: 'translateY(-4px)',
                        }}
                      >
                        {agreement?.formData?.fullName ||
                          authUser?.displayName ||
                          'Franchisee Name'}
                      </p>
                      <p
                        style={{
                          margin: '6px 0 0 0',
                          letterSpacing: '4px',
                          fontSize: '10px',
                          color: cardTheme.faint,
                          textTransform: 'uppercase',
                          transform: 'translateY(-4px)',
                        }}
                      >
                        EXCLUSIVE PARTNER - DOUGLAS & McCARTNEY
                      </p>
                    </div>

                    <div
                      style={{
                        marginTop: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                          gap: '12px',
                        }}
                      >
                        {[
                          { label: 'FRANCHISEE ID', value: franchiseeIdDisplay },
                          {
                            label: 'TERRITORY',
                            value: agreement?.formData?.city || 'India',
                          },
                          {
                            label: 'RERA NO.',
                            value: agreement?.formData?.reraNumber || 'N/A',
                          },
                          { label: 'VALIDITY', value: cardValidityDisplay },
                        ].map((item) => (
                          <div
                            key={item.label}
                            style={{
                              border: `1px solid ${cardTheme.soft}`,
                              padding: '10px 12px',
                              borderRadius: '12px',
                              background: cardTheme.panel,
                            }}
                          >
                            <p
                              style={{
                                margin: 0,
                                fontSize: '9px',
                                letterSpacing: '2px',
                                color: cardTheme.faint,
                              }}
                            >
                              {item.label}
                            </p>
                            <p
                              style={{
                                margin: '6px 0 0 0',
                                fontSize: '12px',
                                fontWeight: 600,
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                              }}
                            >
                              {item.value}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '14px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: '9px',
                              letterSpacing: '2px',
                              color: cardTheme.faint,
                            }}
                          >
                            CONTACT
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                            {agreement?.formData?.email || authUser?.email}
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                            {agreement?.formData?.phone ||
                              agreement?.formData?.mobile ||
                              'N/A'}
                          </p>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                          width: '100%',
                        }}
                      >
                        <div style={{ textAlign: 'right' }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: '9px',
                              letterSpacing: '2px',
                              color: cardTheme.faint,
                            }}
                          >
                            ISSUED BY
                          </p>
                          <p
                            style={{
                              margin: '4px 0 0 0',
                              fontSize: '10px',
                              letterSpacing: '2px',
                              color: cardTheme.text,
                            }}
                          >
                            DOUGLAS & McCARTNEY BOARD
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  id="franchisee-card-back"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '26px',
                    background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 50%, ${cardTheme.base} 100%)`,
                    border: `1px solid ${cardTheme.border}`,
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    overflow: 'hidden',
                    boxShadow: cardTheme.shadow,
                    color: cardTheme.text,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `radial-gradient(circle at 30% 30%, ${cardTheme.glow}, transparent 60%), radial-gradient(circle at 70% 70%, ${cardTheme.soft}, transparent 60%)`,
                      opacity: 0.5,
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: '18px',
                      borderRadius: '20px',
                      border: `1px solid ${cardTheme.soft}`,
                      opacity: 0.35,
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/Douglas2-removebg-preview.png"
                      alt="Douglas & McCartney"
                      style={{
                        width: '200px',
                        height: '200px',
                        objectFit: 'contain',
                        filter: 'brightness(1.1)',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {cardGenerated && agreement?.franchiseeCardGeneratedAt && (
              <div
                style={{
                  marginTop: '20px',
                  padding: '14px 24px',
                  background: cardTheme.panel,
                  border: `1px solid ${cardTheme.soft}`,
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '12px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: cardTheme.muted,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{ color: cardTheme.text }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span style={{ fontWeight: '600', color: cardTheme.text }}>
                  OFFICIAL FRANCHISEE CARD - GENERATED ON {cardIssuedDate}
                </span>
              </div>
            )}

            <button
              onClick={downloadFranchiseeCardPdf}
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '18px',
                background: '#f8f8f8',
                color: '#0a0a0a',
                border: '1px solid #f8f8f8',
                borderRadius: 0,
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                fontFamily:
                  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
              onMouseOver={(event) => {
                event.currentTarget.style.background = '#0a0a0a';
                event.currentTarget.style.color = '#f8f8f8';
                event.currentTarget.style.transform = 'translateY(-2px)';
                event.currentTarget.style.boxShadow = '0 14px 40px rgba(0, 0, 0, 0.6)';
              }}
              onMouseOut={(event) => {
                event.currentTarget.style.background = '#f8f8f8';
                event.currentTarget.style.color = '#0a0a0a';
                event.currentTarget.style.transform = 'translateY(0)';
                event.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.45)';
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                />
              </svg>
              Download Franchisee Card PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const condition = authUser => !!authUser;

export default compose(
  withEmailVerification,
  withAuthorization(condition),
  withFirebase,
)(FranchiseDashboardPageBase);
